/* eslint-disable no-underscore-dangle */
import {fromJS, List, Set} from 'immutable';
import QuestionTypeStore from 'components/QuestionTypes/QuestionType.store';

import multipleChoiceQuestionActions from './MultipleChoiceQuestion.actions';

export default class MultipleChoiceQuestionStore extends QuestionTypeStore {
  constructor(name, questionId, question) {
    super(name, questionId);

    this.initialData = fromJS({
      ...this.draftGuessInitialData,
      questionId,
      isSelectMultiple: question.isSelectMultiple(),
      selectedOptions: [],
      shouldShowExplanation: false,
      eliminatedOptions: [], // Converted to Immutable.Set
      hidingEliminatedOptions: false
    });

    this.setInitialData(this.initialData);

    this.writeData('__question', question);

    this.writeData('eliminatedOptions', this.getEliminatedOptions()?.toSet?.() || Set());

    this.handleTargeted(multipleChoiceQuestionActions.SET_SELECT_MULTIPLE, this._setSelectMultiple);
    this.handleTargeted(multipleChoiceQuestionActions.SELECT_OPTION, this._selectOption);
    this.handleTargeted(
      multipleChoiceQuestionActions.TOGGLE_ELIMINATE_OPTION,
      this._toggleEliminateOption
    );
    this.handleTargeted(multipleChoiceQuestionActions.TOGGLE_HIDE_SHOW, this._toggleHideShowButton);
    this.handleTargeted(
      multipleChoiceQuestionActions.SET_ELIMINATED_TO_EMPTY,
      this._setEliminatedToEmpty
    );
    this.handleTargeted(
      multipleChoiceQuestionActions.REMOVE_OPTION_FROM_SELECTED,
      this._removeOptionFromSelected
    );
  }

  afterLoadDraftGuess(draftGuess) {
    this.writeData('selectedOptions', this.parseGuess(draftGuess));
  }

  areOptionsEliminated() {
    return !this.getEliminatedOptions().isEmpty();
  }

  buildEliminatedOptions() {
    const eliminatedOptions = this.getEliminatedOptions();
    return eliminatedOptions.reduce((acc, option) => {
      acc[option] = true;
      return acc;
    }, {});
  }

  buildGuessContent() {
    const selectedOptions = this.readData('selectedOptions');
    return selectedOptions.reduce((acc, option) => {
      acc[option] = true;
      return acc;
    }, {});
  }

  _clearGuess() {
    super._clearGuess();

    this.writeData('selectedOptions', this.initialData.get('selectedOptions'));
    this._setEliminatedToEmpty();
    super.kickoffDebouncedSaveDraftGuess();
  }

  get correctAnswer() {
    return this.validResponse.keySeq().toSet();
  }

  getEliminatedOptions() {
    return this.readData('eliminatedOptions');
  }

  getHideShow() {
    return this.readData('hidingEliminatedOptions');
  }

  get selectedAnswer() {
    return this.readData('selectedOptions').toSet();
  }

  isOptionEliminated(optionId) {
    return this.getEliminatedOptions().includes(optionId);
  }

  isSelectedOption(id) {
    return this.readData('selectedOptions')?.includes(id) ?? false;
  }

  _loadGuess(guess) {
    super._loadGuess(guess);
    this.writeData('selectedOptions', this.parseGuess(guess));
  }

  parseGuess(guess) {
    const content = guess?.get?.('content');
    if (!content) {
      return new List();
    }
    return content.map((val, optionId) => optionId);
  }

  _removeOptionFromSelected(optionId) {
    const selectedOptions = this.readData('selectedOptions').toSet();
    this.writeData('selectedOptions', selectedOptions.delete(optionId).toList());
    super.kickoffDebouncedSaveDraftGuess();
  }

  _selectOption(id) {
    const isSelectMultiple = this.readData('isSelectMultiple');
    if (!isSelectMultiple) {
      this.writeData('selectedOptions', new List([id]));
      super.kickoffDebouncedSaveDraftGuess();
      return;
    }

    const selectedOptions = this.readData('selectedOptions').toSet();
    if (selectedOptions.has(id)) {
      this.writeData('selectedOptions', selectedOptions.delete(id).toList());
    } else {
      this.writeData('selectedOptions', selectedOptions.add(id).toList());
    }

    super.kickoffDebouncedSaveDraftGuess();
  }

  _setEliminatedToEmpty() {
    this.writeData((store) =>
      store.set('eliminatedOptions', Set()).set('hidingEliminatedOptions', false)
    );
  }

  _setSelectMultiple(val) {
    this.writeData('isSelectMultiple', val);
  }

  _toggleEliminateOption(optionId) {
    let newElimintedOptions;
    if (this.isOptionEliminated(optionId)) {
      newElimintedOptions = this.getEliminatedOptions().delete(optionId);
    } else {
      newElimintedOptions = this.getEliminatedOptions().add(optionId);
    }
    this.writeData('eliminatedOptions', newElimintedOptions);
  }

  _toggleHideShowButton() {
    this.writeData('hidingEliminatedOptions', !this.getHideShow());
  }
}
